import React, { useEffect } from 'react'
import parse, { domToReact } from 'html-react-parser'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { useIntl } from 'react-intl'
import {
  ousterBlogDetail,
  wrapper,
  whiteBackDrop,
  title,
  date,
  author,
  heroContainer,
  condensedMeta,
  hero,
  postContainer,
  divider,
  content,
  leftArrow,
  backLink,
  authorImg,
  authorMeta,
  authorMetaTop,
  authorMetaName,
  authorMetaTitle,
  authorMetaBottom,
  blackCircle,
} from './index.module.css'
import MainBlackHeader from '../MainBlackHeader'
import WPBlogRelatedPosts from '../WPBlogRelatedPosts'
import PostCode from './PostCode'
import WpTags from '../WpTags'
import SEO from '../SEO'
import Image from '../Image'
import Link from '../Link'
import ArrowRight from '../../../assets/images/ArrowRightNoFill.svg'
import li from '../../../static/li_blog.png'
import tw from '../../../static/tw_blog.png'
import { isSvgOrGif } from '../../utils'

const getLanguage = (node) => {
  if (node.attribs.class != null) {
    return node.attribs.class
      .replace('wp-block-code', '')
      .replace('has-normal-font-size', '')
      .trim()
      .toString()
  }
  return null
}

const getCode = (node) => {
  if (node.children.length > 0 && node.children[0].name === 'code') {
    return node.children[0].children
  } else {
    return node.children
  }
}

const replaceCode = (node) => {
  if (node.name === 'pre') {
    return (
      node.children.length > 0 && (
        <PostCode language={getLanguage(node)}>
          {domToReact(getCode(node))}
        </PostCode>
      )
    )
  }
}

const WpBlog = ({ data, relatedData, defaultdata, authorData, location }) => {
  const profile = authorData.userProfile
    ? authorData.userProfile.authorProfile
    : authorData
  const intl = useIntl()
  const mutatedData = data
  const postDate = mutatedData.date
  const blogTitle = intl.formatMessage({ id: 'blog.title' })
  const backToBlog = intl.formatMessage({ id: 'blog.back' })
  const homeUrl = intl.formatMessage({ id: 'blog.homeUrl' })
  const twitterUrl = profile
    ? profile.authorDetails.socialLinks.twitterUrl
    : null
  const linkedinUrl = profile
    ? profile.authorDetails.socialLinks.linkedinUrl
    : null
  const defaultAuthor = intl.formatMessage({ id: 'blog.defaultAuthor' })
  const authorName = profile ? profile.title : defaultAuthor

  useEffect(() => {
    // Reset ordered list numbers
    document.querySelectorAll('ol').forEach((ol) => {
      const start = ol.getAttribute('start')
      if (start) {
        ol.style.counterReset = `item ${Number(start) - 1}`
      }
    })
    document
      .querySelectorAll('.wp-block-image figure, .wp-block-gallery figure')
      .forEach((img) => {
        const span = img.querySelector('.inline-gatsby-image-wrapper')
        if (span) {
          span.style.maxWidth = span.style.width
          span.style.width = '100%'
        }
      })
  }, [])

  return (
    <>
      <SEO
        {...mutatedData}
        seoTitle={
          mutatedData.postOptions.metaTitle
            ? mutatedData.postOptions.metaTitle
            : mutatedData.title
        }
        description={
          mutatedData.postOptions.metaDescription
            ? mutatedData.postOptions.metaDescription
            : mutatedData.excerpt
        }
        seoImage={
          mutatedData?.featuredImage?.node?.localFile?.childImageSharp
            ?.gatsbyImageData
        }
        slug={`${homeUrl}${mutatedData.slug}`}
      />
      <MainBlackHeader title={blogTitle} ctaUrl={homeUrl} secondaryMode />
      <div className={ousterBlogDetail}>
        <div className={wrapper}>
          <div className={backLink}>
            <Link to={homeUrl}>
              <ArrowRight className={leftArrow} />
              {backToBlog}
            </Link>
          </div>
          <div className={whiteBackDrop}>
            <>
              <div className={heroContainer}>
                <div className={hero}>
                  {mutatedData.featuredImage &&
                  mutatedData.featuredImage.node.localFile.childImageSharp ? (
                    <GatsbyImage
                      image={getImage(
                        mutatedData.featuredImage.node.localFile.childImageSharp
                      )}
                      alt={mutatedData?.featuredImage?.node?.altText}
                      loading={'eager'}
                    />
                  ) : isSvgOrGif(
                      mutatedData?.featuredImage?.node?.localFile?.publicURL
                    ) ? (
                    <img
                      src={
                        mutatedData?.featuredImage?.node?.localFile?.publicURL
                      }
                      alt={mutatedData?.featuredImage?.node?.altText}
                    />
                  ) : (
                    <Image
                      source={defaultdata.authorBannerDefault.localFile}
                      alt={defaultdata?.authorBannerDefault?.altText}
                      style={{ objectFit: 'cover' }}
                      objectFit="cover"
                    />
                  )}
                </div>
                <div className={content}>
                  {data.tags.nodes.length > 0 && (
                    <WpTags tags={data.tags.nodes} location={location} />
                  )}
                  <div className={condensedMeta}>
                    <div className={date}>{postDate}</div>
                    <h1 className={title}>{mutatedData.title}</h1>
                    <div className={author}>
                      <div className={authorImg}>
                        {profile && profile.authorDetails.profilePicture ? (
                          <Image
                            className={blackCircle}
                            source={
                              profile.authorDetails.profilePicture.localFile
                            }
                            altText={
                              profile.authorDetails.profilePicture.altText
                            }
                          />
                        ) : (
                          <Image
                            source={defaultdata.authorProfileDefault.localFile}
                            alt={defaultdata.authorProfileDefault.altText}
                            className={blackCircle}
                          />
                        )}
                      </div>
                      <div className={authorMeta}>
                        <div className={authorMetaTop}>
                          {profile ? (
                            <Link
                              to={`${homeUrl}author/${profile.slug + '/'}`}
                              className={authorMetaName}
                            >
                              {authorName}
                            </Link>
                          ) : (
                            <p className={authorMetaName}>{authorName}</p>
                          )}
                          {profile && profile.authorDetails.jobTitle && (
                            <div className={authorMetaTitle}>
                              {profile.authorDetails.jobTitle}
                            </div>
                          )}
                        </div>
                        {(linkedinUrl || twitterUrl) && (
                          <div className={authorMetaBottom}>
                            {linkedinUrl && (
                              <a
                                href={linkedinUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <img src={li} alt="Linkedin Url" />
                              </a>
                            )}
                            {twitterUrl && (
                              <a
                                href={twitterUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <img src={tw} alt="Twitter Url" />
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>

            <div className={postContainer}>
              <div className={divider}></div>
              <div className={content}>
                {parse(mutatedData.content, {
                  replace: replaceCode,
                })}
              </div>
            </div>
          </div>
        </div>
        {relatedData.length > 0 && (
          <WPBlogRelatedPosts
            relatedData={relatedData}
            defaultdata={defaultdata}
            data={data}
            location={location}
          />
        )}
      </div>
    </>
  )
}

export default WpBlog
